import React, { useState, useRef, useEffect } from 'react';
import DefaultLayout from '../../layouts/defaultLayout';
import './style.css';

const MovableComponent = ({ id, position, setPosition, type, onDelete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(id);

  const handleMouseDown = (event) => {
    if (isEditing) return;
    event.preventDefault();
    setIsDragging(true);
    setStartPosition({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    event.preventDefault();
    const deltaX = event.clientX - startPosition.x;
    const deltaY = event.clientY - startPosition.y;
    const newX = Math.max(0, Math.min(position.x + deltaX, 800 - 100));
    const newY = Math.max(0, Math.min(position.y + deltaY, 600 - 40));

    setPosition({ x: newX, y: newY });
    setStartPosition({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    // Here you would typically save the new value
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(id);
      setIsEditing(false);
    }
  };

  return (
    <div
      className={`movable-component ${isDragging ? 'dragging' : ''}`}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: isDragging ? 1000 : 10,
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onDoubleClick={handleDoubleClick}
    >
      <div className="movable-content">
        {isEditing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyPress}
            className="edit-input"
            autoFocus
          />
        ) : (
          <span className="movable-text">{editValue}</span>
        )}
        <button 
          className="delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          ×
        </button>
      </div>
      <div className="component-type">{type}</div>
    </div>
  );
};

const CardContainer = () => {
  const containerRef = useRef(null);
  const [components, setComponents] = useState([
    { id: 1, type: 'ID', label: 'Student ID', position: { x: 50, y: 50 } },
    { id: 2, type: 'CODE', label: 'Course Code', position: { x: 200, y: 100 } },
  ]);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleComponentMove = (id, newPosition) => {
    setComponents(prev => 
      prev.map(comp => 
        comp.id === id ? { ...comp, position: newPosition } : comp
      )
    );
  };

  const handleAddComponent = (type) => {
    const newComponent = {
      id: Date.now(),
      type: type,
      label: type === 'ID' ? 'New ID' : 'New Code',
      position: { x: Math.random() * 300 + 50, y: Math.random() * 200 + 50 }
    };
    setComponents(prev => [...prev, newComponent]);
  };

  const handleDeleteComponent = (id) => {
    setComponents(prev => prev.filter(comp => comp.id !== id));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
        setBackgroundImage(e.target.result);
    };
    reader.readAsDataURL(file);
    }
  };

  const handleClearBackground = () => {
    setBackgroundImage(null);
  };

  return (
    <div className="card-designer-container">
      <div className="designer-toolbar">
        <div className="toolbar-section">
          <h3>Card Designer</h3>
          <div className="button-group">
            <button 
              className="btn btn-primary"
              onClick={() => handleAddComponent('ID')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Add ID Field
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => handleAddComponent('CODE')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Add Code Field
            </button>
          </div>
        </div>
        
        <div className="toolbar-section">
          <label className="file-input-label">
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageUpload}
              className="file-input"
            />
            <span className="file-input-text">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              Upload Background
            </span>
          </label>
          {backgroundImage && (
            <button 
              className="btn btn-danger"
              onClick={handleClearBackground}
            >
              Clear Background
            </button>
          )}
        </div>
      </div>

      <div className="card-canvas" ref={containerRef}>
        <div 
          className="card-background"
      style={{
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          {!backgroundImage && (
            <div className="background-placeholder">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              <p>Upload a background image to get started</p>
            </div>
          )}
      </div>
        
        {components.map((component) => (
          <MovableComponent
            key={component.id}
            id={component.label}
            position={component.position}
            setPosition={(newPosition) => handleComponentMove(component.id, newPosition)}
            type={component.type}
            onDelete={() => handleDeleteComponent(component.id)}
          />
        ))}
        </div>
    </div>
  );
};

const ChainsCourses = () => {
  const [searchkey, setSearchHeaderKey] = useState();
  return (
    <div id="home">
      <DefaultLayout
        setSearchHeaderKey={setSearchHeaderKey}
        children={
          <>
            <CardContainer />
          </>
        }
      />
    </div>
  );
};

export default ChainsCourses;
